import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  LocalShipping as ShipIcon,
  DoneAll as DeliveredIcon,
  ChatBubbleOutline as ChatIcon,
  StickyNote2Outlined as NoteIcon,
} from '@mui/icons-material';
import { formatPrice, resolveOrderCurrency } from '../../../utils/currencyUtils';
import { fulfilmentChipColor, fulfilmentStatusOf, isLateShipment } from '../../../utils/storeFulfilment';
import StorePayoutStatus from '../StorePayoutStatus';
import { orderStatusLabel, orderStatusColor, completeAwaitsCapture, completeAwaitsEnd } from '../../../utils/orderStatusLabel';
import { calculateOrderTotal, getOrderDetails, getOrderTypeDisplay, getPaymentChip } from './orderDisplay';
import { canPerformAction } from './useProviderOrders';

// Sortable columns for the orders table
const ORDER_COLUMNS = [
  { id: 'id', labelKey: 'orders.colId', sortable: true },
  { id: 'customer', labelKey: 'orders.colCustomer', sortable: true },
  { id: 'type', labelKey: 'orders.colType', sortable: true },
  { id: 'total', labelKey: 'orders.colTotal', sortable: false },
  { id: 'status', labelKey: 'orders.colStatus', sortable: true },
  { id: 'payment', labelKey: 'orders.colPayment', sortable: false },
  { id: 'date', labelKey: 'orders.colDate', sortable: true },
  { id: 'actions', labelKey: 'orders.colActions', sortable: false },
];

const getOrderSortValue = (order, key) => {
  switch (key) {
    case 'id':
      return order.id ?? 0;
    case 'customer':
      return (order.user?.name || '').toLowerCase();
    case 'type':
      return (order.orderType === 'STORE'
        ? 'store order'
        : (order.dogServiceType || '').replace(/_/g, ' ')).toLowerCase();
    case 'status':
      return (order.status || '').toLowerCase();
    case 'date':
      return order.createdAt ? new Date(order.createdAt).getTime() : 0;
    default:
      return 0;
  }
};

const compareOrders = (a, b, key, dir) => {
  const va = getOrderSortValue(a, key);
  const vb = getOrderSortValue(b, key);
  let cmp = 0;
  if (va < vb) cmp = -1;
  else if (va > vb) cmp = 1;
  return dir === 'desc' ? -cmp : cmp;
};

/**
 * The fulfilment chip (+ late badge) for a STORE order. Renders nothing for SERVICE orders and
 * for store orders paid before fulfilment tracking existed (fulfilment: null) so old rows look as
 * they always did.
 */
export const FulfilmentStatus = ({ order }) => {
  const { t } = useTranslation();
  const status = fulfilmentStatusOf(order);
  if (!status) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }} data-testid={`fulfilment-status-${order.id}`}>
      <Chip
        label={t(`fulfilment.status${status}`)}
        color={fulfilmentChipColor(status)}
        size="small"
        variant="outlined"
      />
      {isLateShipment(order) && (
        <Chip label={t('fulfilment.late')} color="error" size="small" />
      )}
    </Box>
  );
};

/**
 * Props: orders (already filtered), txByOrder, notesSummary, actionLoading,
 * onViewDetails(order), onQuickNote(order), onChat(order), onComplete(orderId), onShip(order),
 * onDeliver(order), onCancel(order).
 */
export default function OrdersTable({
  orders, txByOrder, notesSummary = {}, actionLoading,
  onViewDetails, onQuickNote, onChat, onComplete, onShip, onDeliver, onCancel,
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Sorting: newest first by default
  const [orderBy, setOrderBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const sortedOrders = useMemo(() => [...orders].sort((a, b) => compareOrders(a, b, orderBy, sortDir)), [orders, orderBy, sortDir]);
  // Filters can shrink the set under the current page; clamp instead of showing an empty page.
  const safePage = Math.min(page, Math.max(0, Math.ceil(sortedOrders.length / rowsPerPage) - 1));
  const pagedOrders = useMemo(
    () => sortedOrders.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [sortedOrders, safePage, rowsPerPage],
  );

  const handleRequestSort = (property) => {
    if (orderBy === property) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(property);
      // Default to descending for date, ascending otherwise
      setSortDir(property === 'date' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
              {ORDER_COLUMNS.map((col) => (
                <TableCell key={col.id} sortDirection={orderBy === col.id ? sortDir : false}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? sortDir : 'asc'}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      <strong>{t(col.labelKey)}</strong>
                    </TableSortLabel>
                  ) : (
                    <strong>{t(col.labelKey)}</strong>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedOrders.map((order) => {
              const orderDetails = getOrderDetails(order, t);
              const total = calculateOrderTotal(order);
              const orderTxs = txByOrder[order.id];
              const note = notesSummary[order.id];

              return (
                <TableRow key={order.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => onViewDetails(order)}
                        sx={{ textTransform: 'none' }}
                      >
                        #{order.id}
                      </Button>
                      <Tooltip title={note ? note.latestBody.slice(0, 120) : t('orders.notes.quickNote')}>
                        <IconButton
                          size="small"
                          aria-label={t('orders.notes.quickNote')}
                          data-testid={`note-indicator-${order.id}`}
                          onClick={() => onQuickNote(order)}
                        >
                          <Badge badgeContent={note?.count || 0} color="primary" max={99}>
                            <NoteIcon fontSize="small" color={note ? 'primary' : 'disabled'} />
                          </Badge>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {order.user?.name || t('orders.na')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('orders.userId')}: {order.user?.id || t('orders.na')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {getOrderTypeDisplay(order, t)}
                      </Typography>
                      {order.orderType === 'STORE' && (
                        <Typography variant="caption" color="text.secondary">
                          {t('orders.itemCount', { count: orderDetails.itemCount })}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatPrice(total || 0, resolveOrderCurrency(order))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={orderStatusLabel(order, t)}
                      color={orderStatusColor(order)}
                      size="small"
                    />
                    <FulfilmentStatus order={order} />
                    <StorePayoutStatus order={order} />
                  </TableCell>
                  <TableCell>
                    {getPaymentChip(orderTxs, t)}
                  </TableCell>
                  <TableCell>
                    {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : t('orders.na')}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ChatIcon />}
                        onClick={() => onChat(order)}
                      >
                        {t('orders.chatQuote')}
                      </Button>
                      {canPerformAction(order, 'complete') && (
                        <Tooltip title={completeAwaitsEnd(order) ? t('orders.completeAfterEnd', { date: new Date(order.expectedCompletionAt).toLocaleString() }) : (completeAwaitsCapture(order) ? t('orders.completeAfterCapture') : '')}>
                          {/* span: a disabled button fires no events, so the tooltip needs a wrapper */}
                          <span>
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<CompleteIcon />}
                              onClick={() => onComplete(order.id)}
                              disabled={actionLoading || completeAwaitsCapture(order) || completeAwaitsEnd(order)}
                              color="success"
                            >
                              {t('orders.completeButton')}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      {canPerformAction(order, 'markShipped') && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ShipIcon />}
                          onClick={() => onShip(order)}
                          disabled={actionLoading}
                          color="primary"
                        >
                          {t('fulfilment.markShipped')}
                        </Button>
                      )}
                      {canPerformAction(order, 'markDelivered') && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DeliveredIcon />}
                          onClick={() => onDeliver(order)}
                          disabled={actionLoading}
                          color="success"
                        >
                          {t('fulfilment.markDelivered')}
                        </Button>
                      )}
                      {canPerformAction(order, 'cancel') && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<CancelIcon />}
                          onClick={() => onCancel(order)}
                          disabled={actionLoading}
                          color="error"
                        >
                          {t('common.cancel')}
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sortedOrders.length}
        page={safePage}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25]}
      />
    </>
  );
}
